import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PasswordInput from "@/components/PasswordInput";

const MIN = 5;
const MAX = 10;

/**
 * RotatePasscode — admin-only inline panel that changes the shared back-office
 * passcode without DB access. Requires the current passcode as proof, except
 * when the session was created via a one-time temporary passcode (via_temp),
 * in which case the current-passcode field is skipped.
 */
export default function RotatePasscode() {
  const { user } = useAuth();
  const viaTemp = !!user?.via_temp;
  const [open, setOpen] = useState(viaTemp); // auto-open when recovering
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type, text}

  const valid = (s) => s.length >= MIN && s.length <= MAX;

  const submit = async () => {
    setFeedback(null);
    if (!viaTemp && !valid(current)) {
      setFeedback({ type: "error", text: `Current passcode must be ${MIN}–${MAX} characters.` });
      return;
    }
    if (!valid(next)) {
      setFeedback({ type: "error", text: `New passcode must be ${MIN}–${MAX} characters.` });
      return;
    }
    if (next !== confirm) {
      setFeedback({ type: "error", text: "New passcodes do not match." });
      return;
    }
    if (!viaTemp && current === next) {
      setFeedback({ type: "error", text: "New passcode must differ from the current one." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/back-office/rotate", {
        current_passcode: viaTemp ? "" : current,
        new_passcode: next,
      });
      setFeedback({ type: "success", text: "Passcode rotated. Share the new one with staff." });
      setCurrent("");
      setNext("");
      setConfirm("");
      if (viaTemp) {
        // Force a reload so the temp banner clears (it's keyed off via_temp from the JWT).
        setTimeout(() => { window.location.reload(); }, 1200);
      } else {
        setTimeout(() => setOpen(false), 1800);
      }
    } catch (err) {
      setFeedback({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative inline-block" data-testid="rotate-passcode">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="rotate-passcode-toggle"
        className={`inline-flex items-center gap-2 px-3 py-1.5 text-[0.7rem] tracking-[0.18em] uppercase border font-semibold transition-colors ${
          viaTemp
            ? "border-[#8a1a1a] text-[#8a1a1a] bg-[#fbe9e9] hover:bg-[#f5d5d5]"
            : "border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8] hover:bg-[#F3ECD6]"
        }`}
      >
        {viaTemp ? "Set New Passcode" : "Rotate Passcode"}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 z-30 surface-card p-5 fade-up"
          data-testid="rotate-passcode-panel"
        >
          <div className="flex items-center justify-between">
            <div className="overline">{viaTemp ? "Recovery" : "Staff Entrance"}</div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setFeedback(null);
                setCurrent("");
                setNext("");
                setConfirm("");
              }}
              className="text-xs text-[#7A7A7A] hover:text-[#1A422D] uppercase tracking-wider"
            >
              Close
            </button>
          </div>
          <h3 className="font-serif text-xl mt-1 text-[#1A1A1A]">
            {viaTemp ? "Set a New Passcode" : "Rotate Passcode"}
          </h3>
          <p className="text-xs text-[#7A7A7A] mt-1 leading-relaxed">
            {viaTemp
              ? `You're signed in with a temporary passcode. Choose a new permanent one (${MIN}–${MAX} characters).`
              : `Confirm the current passcode, then choose a new one (${MIN}–${MAX} characters).`}
          </p>

          <div className="mt-4 space-y-3">
            {!viaTemp && (
              <div>
                <label className="label-elegant block mb-1">Current Passcode</label>
                <PasswordInput
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  maxLength={MAX}
                  data-testid="rotate-current"
                  className="input-field tracking-[0.4em] text-center"
                  placeholder="•••••"
                />
              </div>
            )}
            <div>
              <label className="label-elegant block mb-1">New Passcode</label>
              <PasswordInput
                value={next}
                onChange={(e) => setNext(e.target.value)}
                maxLength={MAX}
                data-testid="rotate-new"
                className="input-field tracking-[0.4em] text-center"
                placeholder="•••••"
              />
            </div>
            <div>
              <label className="label-elegant block mb-1">Confirm New</label>
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                maxLength={MAX}
                data-testid="rotate-confirm"
                className="input-field tracking-[0.4em] text-center"
                placeholder="•••••"
              />
            </div>
          </div>

          {feedback && (
            <div
              className={`mt-4 px-3 py-2 text-xs border ${
                feedback.type === "success"
                  ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                  : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
              }`}
            >
              {feedback.text}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid="rotate-submit"
            className="btn-primary w-full mt-4"
          >
            {busy ? "Saving…" : "Save New Passcode"}
          </button>
        </div>
      )}
    </div>
  );
}
